import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const UserManagementTab = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);

  const users = [
    {
      id: 1,
      name: "Sarah Johnson",
      email: "sarah.johnson@cyberguard.gov",
      role: "Senior Analyst",
      department: "Incident Response",
      status: "Active",
      lastLogin: "2025-09-19 09:15:00",
      permissions: ["view_incidents", "create_incident", "edit_incident"]
    },
    {
      id: 2,
      name: "Michael Chen",
      email: "michael.chen@cyberguard.gov",
      role: "SOC Manager",
      department: "Security Operations",
      status: "Active",
      lastLogin: "2025-09-19 08:30:00",
      permissions: ["view_dashboard", "manage_users", "view_reports"]
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      email: "emily.rodriguez@cyberguard.gov",
      role: "Compliance Officer",
      department: "Compliance",
      status: "Inactive",
      lastLogin: "2025-09-18 16:45:00",
      permissions: ["view_reports", "export_data", "audit_logs"]
    },
    {
      id: 4,
      name: "David Thompson",
      email: "david.thompson@cyberguard.gov",
      role: "Junior Analyst",
      department: "Incident Response",
      status: "Active",
      lastLogin: "2025-09-19 10:20:00",
      permissions: ["view_incidents", "create_incident"]
    }
  ];

  const roles = [
    { value: '', label: 'All Roles' },
    { value: 'admin', label: 'Administrator' },
    { value: 'senior_analyst', label: 'Senior Analyst' },
    { value: 'soc_manager', label: 'SOC Manager' },
    { value: 'compliance_officer', label: 'Compliance Officer' },
    { value: 'junior_analyst', label: 'Junior Analyst' }
  ];

  const departments = [
    { value: 'incident_response', label: 'Incident Response' },
    { value: 'security_operations', label: 'Security Operations' },
    { value: 'compliance', label: 'Compliance' },
    { value: 'threat_intelligence', label: 'Threat Intelligence' }
  ];

  const filteredUsers = users?.filter(user => {
    const matchesSearch = user?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesRole = !selectedRole || user?.role?.toLowerCase()?.includes(selectedRole);
    return matchesSearch && matchesRole;
  });

  const handleUserSelect = (userId) => {
    setSelectedUsers(prev => 
      prev?.includes(userId) 
        ? prev?.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleBulkAction = (action) => {
    console.log(`Performing ${action} on users:`, selectedUsers);
    setSelectedUsers([]);
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    return status === 'Active' 
      ? `${baseClasses} bg-success text-success-foreground`
      : `${baseClasses} bg-muted text-muted-foreground`;
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">User Management</h3>
          <p className="text-sm text-muted-foreground">Manage user accounts, roles, and permissions</p>
        </div>
        <Button 
          variant="default" 
          iconName="Plus" 
          iconPosition="left"
          onClick={() => setShowCreateUser(true)}
        >
          Create User
        </Button>
      </div>
      {/* Search and Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          type="search"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
          className="md:col-span-2"
        />
        <Select
          placeholder="Filter by role"
          options={roles}
          value={selectedRole}
          onChange={setSelectedRole}
        />
      </div>
      {/* Bulk Actions */}
      {selectedUsers?.length > 0 && (
        <div className="flex items-center gap-2 p-4 bg-muted rounded-lg">
          <span className="text-sm text-muted-foreground">
            {selectedUsers?.length} user{selectedUsers?.length > 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2 ml-auto">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handleBulkAction('activate')}
            >
              Activate
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handleBulkAction('deactivate')}
            >
              Deactivate
            </Button>
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => handleBulkAction('delete')}
            >
              Delete
            </Button>
          </div>
        </div>
      )}
      {/* Users Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="w-12 p-4">
                  <Checkbox
                    checked={selectedUsers?.length === filteredUsers?.length}
                    indeterminate={selectedUsers?.length > 0 && selectedUsers?.length < filteredUsers?.length}
                    onChange={(e) => {
                      if (e?.target?.checked) {
                        setSelectedUsers(filteredUsers?.map(u => u?.id));
                      } else {
                        setSelectedUsers([]);
                      }
                    }}
                  />
                </th>
                <th className="text-left p-4 text-sm font-medium text-foreground">User</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Role & Department</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Status</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Last Login</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers?.map((user) => (
                <tr key={user?.id} className="border-t border-border hover:bg-muted/50">
                  <td className="p-4">
                    <Checkbox
                      checked={selectedUsers?.includes(user?.id)}
                      onChange={() => handleUserSelect(user?.id)}
                    />
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                        <Icon name="User" size={16} color="white" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{user?.name}</p>
                        <p className="text-sm text-muted-foreground">{user?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div>
                      <p className="font-medium text-foreground">{user?.role}</p>
                      <p className="text-sm text-muted-foreground">{user?.department}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={getStatusBadge(user?.status)}>
                      {user?.status}
                    </span>
                  </td>
                  <td className="p-4">
                    <p className="text-sm text-foreground">{new Date(user.lastLogin)?.toLocaleDateString()}</p>
                    <p className="text-xs text-muted-foreground">{new Date(user.lastLogin)?.toLocaleTimeString()}</p>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <Button variant="ghost" size="sm" iconName="Edit" />
                      <Button variant="ghost" size="sm" iconName="Key" />
                      <Button variant="ghost" size="sm" iconName="MoreHorizontal" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Create User Modal */}
      {showCreateUser && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg border border-border shadow-medium w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">Create New User</h3>
              <Button 
                variant="ghost" 
                size="sm" 
                iconName="X"
                onClick={() => setShowCreateUser(false)}
              />
            </div>
            <div className="p-6 space-y-4">
              <Input
                label="Full Name"
                type="text"
                placeholder="Enter full name"
                required
              />
              <Input
                label="Email Address"
                type="email"
                placeholder="user@cyberguard.gov"
                required
              />
              <Select
                label="Role"
                placeholder="Select role"
                options={roles?.slice(1)}
                required
              />
              <Select
                label="Department"
                placeholder="Select department"
                options={departments}
                required
              />
              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  variant="outline"
                  onClick={() => setShowCreateUser(false)}
                >
                  Cancel
                </Button>
                <Button variant="default">
                  Create User
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementTab;